/**
 * Utility functions for generating URL slugs from entity and interaction names
 */

/**
 * Converts a string to a URL-friendly slug
 * @param text - The text to convert to a slug
 * @returns A URL-friendly slug
 */
export function createSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
    .replace(/[\s_-]+/g, "-") // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
}

/**
 * Creates a URL slug for an entity
 * @param entity - The entity to create a slug for
 * @returns A slug in the format "entity-name-12characterid"
 */
export function createEntitySlug(entity: { name: string; id: string }): string {
  const slug = createSlug(entity.name);
  // Ensure id is a string and has at least 12 characters
  const id = String(entity.id || "");
  const shortId = id.length >= 12 ? id.slice(-12) : id.padStart(12, "0");
  return `${slug}-${shortId}`;
}

/**
 * Creates a URL slug for an interaction
 * @param interaction - The interaction to create a slug for
 * @returns A slug in the format "interaction-title-12characterid"
 */
export function createInteractionSlug(interaction: {
  title: string;
  id: string;
}): string {
  const slug = createSlug(interaction.title);
  // Ensure id is a string and has at least 12 characters
  const id = String(interaction.id || "");
  const shortId = id.length >= 12 ? id.slice(-12) : id.padStart(12, "0");
  return `${slug}-${shortId}`;
}

/**
 * Creates a URL slug for a view
 * @param view - The view to create a slug for
 * @returns A slug in the format "publicid-view-name"
 */
export function createViewSlug(view: {
  name: string;
  public_id: string;
}): string {
  const slug = createSlug(view.name);
  return `${view.public_id}-${slug}`;
}

/**
 * Extracts the public_id from a view slug (ID is at the start: "publicid-view-name")
 * @param slug - The view slug to extract the ID from
 * @returns The public_id (everything before the first hyphen)
 */
export function extractIdFromSlug(slug: string): string {
  const slugStr = String(slug || "");
  const hyphenIndex = slugStr.indexOf("-");
  return hyphenIndex === -1 ? slugStr : slugStr.slice(0, hyphenIndex);
}

/**
 * Extracts the ID from an entity or interaction slug (ID is at the end: "name-slug-12characterid")
 * Also handles old-style plain numeric IDs for backward compatibility.
 * @param slug - The entity/interaction slug to extract the ID from
 * @returns The ID string
 */
export function extractTrailingIdFromSlug(slug: string): string {
  const slugStr = String(slug || "");
  // Plain numeric ID (old-style URL like /rms/interaction/12345) — return as-is
  if (/^\d+$/.test(slugStr)) return slugStr;
  // Slugged format (name-slug-000000012345) — extract last 12 chars
  return slugStr.slice(-12);
}
